import { GridColumn as Column } from '@progress/kendo-react-grid';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  DeleteButton,
  SecondaryButton,
  TertiaryButton
} from 'src/components/shared/Button/Button';
import { DropdownCell } from 'src/components/shared/DataGrid/EditableGrid/DropdownCell';
import { EditableGrid } from 'src/components/shared/DataGrid/EditableGrid/EditableGrid';
import { selectParameterSets } from 'src/store/parametersets/parametersets.selectors';
import { selectRecipients } from 'src/store/recipients/recipients.selectors';
import {
  SpecificationDetailDto,
  SpecificationRecipientParameterSetDto
} from 'src/store/specifications/specification';
import { isUserAdmin, useAuth } from '../../Auth/AuthContext';

interface Props {
  specification: Nullable<SpecificationDetailDto>;
  onRecipientParameterSetsChanged: (
    recipientParameterSets: SpecificationRecipientParameterSetDto[]
  ) => void;
}

let uniqueId = 0;

export const RecipientParameterSets: React.FC<Props> = ({
  specification,
  onRecipientParameterSetsChanged
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [recipientParameterSets, setRecipientParameterSets] = useState<
    SpecificationRecipientParameterSetDto[]
  >([]);

  const recipients$ = useSelector(selectRecipients);
  const parameterSets$ = useSelector(selectParameterSets);

  useEffect(() => {
    if (specification) {
      setRecipientParameterSets(
        specification.specificationRecipientParameterSets
      );
    }
  }, [specification]);

  useEffect(() => {
    onRecipientParameterSetsChanged(recipientParameterSets);
  }, [recipientParameterSets, onRecipientParameterSetsChanged]);

  const onCancel = () => {
    setRecipientParameterSets([
      ...(specification?.specificationRecipientParameterSets ?? [])
    ]);
  };

  const onAddRecipientParameterSet = () => {
    const recipientParameterSet: SpecificationRecipientParameterSetDto = {
      id: --uniqueId,
      specificationId: specification?.id
    };
    setRecipientParameterSets([
      recipientParameterSet,
      ...recipientParameterSets
    ]);
  };

  const onDeleteRecipientParameterSet = (
    recipientParameterSet: SpecificationRecipientParameterSetDto
  ) => {
    setRecipientParameterSets(
      recipientParameterSets.filter(cps => cps.id !== recipientParameterSet.id)
    );
  };

  const ParametersList = useMemo(() => {
    return (
      <EditableGrid
        items={recipientParameterSets}
        onItemsChange={items => {
          setRecipientParameterSets(items);
        }}
      >
        <Column
          field="recipientId"
          title={t('recipient.singular')}
          width="440px"
          cell={props => (
            <DropdownCell
              {...props}
              items={recipients$}
              disabled={!isUserAdmin(user)}
              uniqueField="id"
              optionField="name"
            />
          )}
        />
        <Column
          field="parameterSetId"
          title={t('parameterSet.singular')}
          width="440px"
          cell={props => (
            <DropdownCell
              {...props}
              items={parameterSets$}
              disabled={!isUserAdmin(user)}
              uniqueField="id"
              optionField="name"
            />
          )}
        />
        <Column
          width="60px"
          filterable={false}
          editable={false}
          cell={props => (
            <td>
              <DeleteButton
                onClick={() => onDeleteRecipientParameterSet(props.dataItem)}
              />
            </td>
          )}
        />
      </EditableGrid>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    recipientParameterSets,
    recipients$,
    parameterSets$,
    onRecipientParameterSetsChanged,
    t
  ]);

  return (
    <Fragment>
      <div className="flex mb-24">
        <SecondaryButton
          icon="la-plus"
          onClick={onAddRecipientParameterSet}
          disabled={!isUserAdmin(user)}
          className="mr-24"
        >
          {t('add.verb')}
        </SecondaryButton>
        <TertiaryButton onClick={onCancel} disabled={!isUserAdmin(user)}>
          {t('undo')}
        </TertiaryButton>
      </div>
      {ParametersList}
    </Fragment>
  );
};
